import React, { useState } from "react";
import { toast } from "sonner";
import { useI18n, formatPrice } from "../i18n";
import { api } from "../api";
import { Copy, PartyPopper, CheckCircle2, ArrowRight } from "lucide-react";

const Welcome = () => {
  const { lang } = useI18n();
  const isPt = lang === "pt";
  const [form, setForm] = useState({ name: "", email: "", arrived_at: "" });
  const [code, setCode] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSubmitting(true);
    try {
      const res = await api.post("/welcome/signup", form);
      setCode(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (v) => {
    navigator.clipboard.writeText(v);
    toast.success(isPt ? "Copiado!" : "Copied!");
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20" data-testid="welcome-page">
      <p className="text-label text-[#FFB400]">
        <span className="line-accent" />
        {isPt ? "Programa Recém-chegado" : "Newcomer Program"}
      </p>
      <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-bold tracking-tighter text-white">
        {isPt ? (
          <>Boas-vindas a Boston. <span className="text-[#FFB400]">$30 off</span> na sua 1ª locação.</>
        ) : (
          <>Welcome to Boston. <span className="text-[#FFB400]">$30 off</span> your first rental.</>
        )}
      </h1>
      <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-2xl">
        {isPt
          ? "Você chegou nos últimos 6 meses? Crie sua conta gratuita e receba um cupom exclusivo para a primeira locação na Movix. Aceitamos CNH brasileira, sem fiador, sem entrada."
          : "Arrived in the last 6 months? Create a free account and receive an exclusive coupon for your first rental. Brazilian license accepted, no co-signer, no down payment."}
      </p>

      {!code ? (
        <form onSubmit={onSubmit} className="mt-12 bg-[#0d0d0d] border border-white/10 p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="welcome-form">
          <div>
            <label className="text-label block mb-2">{isPt ? "Nome" : "Name"} *</label>
            <input
              className="movix-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="welcome-name"
            />
          </div>
          <div>
            <label className="text-label block mb-2">E-mail *</label>
            <input
              type="email"
              className="movix-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              data-testid="welcome-email"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-label block mb-2">
              {isPt ? "Quando você chegou nos EUA?" : "When did you arrive in the US?"}
            </label>
            <input
              type="date"
              className="movix-input"
              value={form.arrived_at}
              onChange={(e) => setForm({ ...form, arrived_at: e.target.value })}
              data-testid="welcome-arrived"
            />
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center disabled:opacity-60"
              data-testid="welcome-submit"
            >
              {submitting ? (isPt ? "Gerando…" : "Generating…") : isPt ? "Gerar meu cupom" : "Get my coupon"}
              <ArrowRight size={16} />
            </button>
          </div>
        </form>
      ) : (
        <div className="mt-12 bg-[#0d0d0d] border border-[#FFB400] p-10" data-testid="welcome-result">
          <PartyPopper size={36} className="text-[#FFB400]" />
          <p className="text-label text-[#FFB400] mt-4">
            {isPt ? "Cupom gerado" : "Coupon generated"}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <span className="font-mono-ui text-4xl sm:text-5xl font-bold text-white tracking-widest" data-testid="welcome-code-value">
              {code.code}
            </span>
            <button onClick={() => copy(code.code)} className="btn-outline py-3" data-testid="welcome-copy">
              <Copy size={14} /> {isPt ? "Copiar" : "Copy"}
            </button>
          </div>
          <p className="mt-6 text-zinc-300 text-lg">
            {isPt
              ? `Aplique este código na sua próxima reserva para ganhar `
              : "Apply this code on your next booking to save "}
            <span className="text-[#FFB400] font-semibold">
              {formatPrice(code.discount_usd)}
            </span>
            {isPt ? " de desconto." : "."}
          </p>
          <ul className="mt-8 space-y-2 text-sm text-zinc-400" data-testid="welcome-perks">
            <li className="flex items-start gap-2">
              <CheckCircle2 size={16} className="text-[#FFB400] mt-0.5" />
              {isPt ? "Válido por 12 meses na primeira locação" : "Valid for 12 months on your first rental"}
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 size={16} className="text-[#FFB400] mt-0.5" />
              {isPt ? "Combina com Aluguel Mensal e Movix em Casa" : "Works with Monthly rental and Movix at Home plans"}
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 size={16} className="text-[#FFB400] mt-0.5" />
              {isPt ? "Comprovante de chegada solicitado no balcão" : "Proof of US arrival requested at the counter"}
            </li>
          </ul>

          <a href="/fleet" className="btn-primary mt-10 inline-flex" data-testid="welcome-fleet-cta">
            {isPt ? "Ver a frota" : "Browse the fleet"} <ArrowRight size={16} />
          </a>
        </div>
      )}
    </div>
  );
};

export default Welcome;
